import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    LayoutDashboard,
    Film,
    HardDrive,
    Truck,
    Users,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Database,
    Building2,
    Crown,
    FileUp,
    Sun,
    Moon,
    ClipboardList
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const navigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Projets', href: '/projects', icon: Film },
    { name: 'Masters', href: '/masters', icon: HardDrive },
    { name: 'Mouvements', href: '/movements', icon: Truck },
    { name: 'État du stock', href: '/stock', icon: ClipboardList },
];

const adminNavigation = [
    { name: 'Utilisateurs', href: '/users', icon: Users },
];

const superAdminNavigation = [
    { name: 'Gestion Clients', href: '/companies', icon: Building2 },
    { name: 'Import Données', href: '/import', icon: FileUp },
];

export const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const sidebarRef = useRef(null);
    const menuButtonRef = useRef(null);
    const { user, logout, isAdmin, isSuperAdmin, activeCompany } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    // Mobile drawer: Escape closes, Tab stays inside, focus moves in on open and back to the
    // menu button on close, and the page behind does not scroll.
    useEffect(() => {
        if (!sidebarOpen) return;
        const sidebar = sidebarRef.current;
        const focusables = () => Array.from(
            sidebar.querySelectorAll('a[href], button:not([disabled])')
        ).filter(el => el.offsetParent !== null);
        focusables()[0]?.focus();

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSidebarOpen(false);
                return;
            }
            if (e.key !== 'Tab') return;
            const items = focusables();
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        const menuButton = menuButtonRef.current;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            menuButton?.focus();
        };
    }, [sidebarOpen]);

    // Close the drawer if the viewport grows to the desktop layout while it is open
    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 1024px)');
        const onChange = (e) => { if (e.matches) setSidebarOpen(false); };
        desktop.addEventListener('change', onChange);
        return () => desktop.removeEventListener('change', onChange);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    let allNavigation = [...navigation];
    if (isAdmin()) {
        allNavigation = [...allNavigation, ...adminNavigation];
    }
    if (isSuperAdmin()) {
        allNavigation = [...superAdminNavigation, ...allNavigation];
    }

    return (
        <div className="min-h-screen bg-background">
            <a
                href="#contenu"
                className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-sm focus:bg-primary focus:text-primary-foreground focus:font-medium"
            >
                Aller au contenu
            </a>
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 z-40 bg-black/60 lg:hidden animate-fade-in"
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                ref={sidebarRef}
                aria-label="Navigation principale"
                role={sidebarOpen ? 'dialog' : undefined}
                aria-modal={sidebarOpen ? true : undefined}
                className={cn(
                    "fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-card border-r border-border transform lg:translate-x-0 lg:visible motion-reduce:transition-none",
                    // Visible at once when opening (so focus can move in); hidden only after the slide-out.
                    // Opens with a decelerating 250ms slide, closes faster (180ms).
                    sidebarOpen
                        ? "translate-x-0 visible transition-transform [transition-duration:250ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
                        : "-translate-x-full invisible transition-[transform,visibility] [transition-duration:180ms] [transition-timing-function:cubic-bezier(0.4,0,1,1)]"
                )}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex flex-col px-6 py-5 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded bg-primary/10" aria-hidden="true">
                                <Database className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="font-heading text-xl font-bold tracking-tight text-foreground">
                                    AD-MASTER
                                </p>
                                <p className="text-xs text-muted-foreground">Gestion DCP</p>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Fermer le menu"
                                className="ml-auto lg:hidden"
                                onClick={() => setSidebarOpen(false)}
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                        
                        {/* SuperAdmin Company Indicator */}
                        {isSuperAdmin() && activeCompany && (
                            <Link 
                                to="/companies"
                                className="mt-3 p-2 rounded bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Crown className="w-4 h-4 text-primary" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-muted-foreground">Client géré :</p>
                                        <p className="text-sm font-semibold text-primary truncate">
                                            {activeCompany.name || 'Sélectionner...'}
                                        </p>
                                    </div>
                                </div>
                            </Link>
                        )}
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
                        {allNavigation.map((item) => {
                            const isActive = location.pathname.startsWith(item.href);
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={cn(
                                        "flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                                        isActive 
                                            ? "bg-primary/10 text-primary border-l-2 border-primary" 
                                            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                    )}
                                >
                                    <item.icon className="w-5 h-5" />
                                    {item.name}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-border space-y-2">
                        <button
                            onClick={toggleTheme}
                            className="flex items-center gap-3 w-full px-3 py-2 touch:py-3 rounded-sm text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                        </button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button 
                                    className="flex items-center gap-3 w-full px-3 py-2 rounded-sm hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    data-testid="user-menu-trigger"
                                >
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-medium text-sm">
                                        {user?.name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {user?.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground capitalize">
                                            {user?.role}
                                        </p>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    onClick={handleLogout}
                                    className="text-destructive-text focus:text-destructive-text cursor-pointer"
                                    data-testid="logout-button"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Déconnexion
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Mobile header */}
                <header className="sticky top-0 z-30 flex items-center gap-4 px-4 py-3 bg-background/80 backdrop-blur border-b border-border lg:hidden">
                    <Button
                        ref={menuButtonRef}
                        variant="ghost"
                        size="icon" aria-label="Ouvrir le menu" aria-expanded={sidebarOpen}
                        onClick={() => setSidebarOpen(true)}
                        data-testid="mobile-menu-button"
                    >
                        <Menu className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-primary" />
                        <span className="font-heading font-bold">AD-MASTER</span>
                    </div>
                </header>

                {/* Page content */}
                <main id="contenu" tabIndex={-1} className="min-h-[calc(100vh-57px)] lg:min-h-screen focus:outline-none">
                    {children}
                </main>
            </div>
        </div>
    );
};
